// src/pages/Settings/MaintenanceMode.jsx
import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import { toast } from 'react-toastify';

const MaintenanceMode = () => {
  const [loading, setLoading] = useState(false);
  const [maintenance, setMaintenance] = useState({
    status: false,
    startTime: '',
    endTime: '',
    message: 'App is under maintenance...'
  });
  const [countdown, setCountdown] = useState('No active maintenance');

  // Fetch maintenance settings
  const fetchMaintenanceSettings = useCallback(async () => {
    try {
      const response = await api.get('/admin/settings/maintenance');
      if (response.data) {
        setMaintenance({
          status: response.data.status || false,
          startTime: response.data.startTime || '',
          endTime: response.data.endTime || '',
          message: response.data.message || 'App is under maintenance...'
        });
      }
    } catch (error) {
      console.error('Error fetching maintenance settings:', error);
      toast.error('Failed to load maintenance settings');
    }
  }, []);

  // Check maintenance status and update countdown
  const checkMaintenanceStatus = useCallback(() => {
    if (!maintenance.endTime || !maintenance.status) {
      setCountdown('No active maintenance');
      return;
    }
    
    const now = new Date();
    const [endHour, endMinute] = maintenance.endTime.split(':');
    const end = new Date();
    end.setHours(parseInt(endHour), parseInt(endMinute), 0);
    
    if (now < end) {
      const diff = end - now;
      const hours = Math.floor(diff / 3600000);
      const mins = Math.floor((diff % 3600000) / 60000);
      const secs = Math.floor((diff % 60000) / 1000);
      setCountdown(`Ends in ${hours}h ${mins}m ${secs}s`);
    } else {
      setCountdown('Maintenance period ended');
      // Auto disable when end time is reached
      if (maintenance.status) {
        handleAutoDisable();
      }
    }
  }, [maintenance.endTime, maintenance.status]);

  // Auto disable maintenance
  const handleAutoDisable = useCallback(async () => {
    try {
      await api.put('/admin/settings/maintenance', {
        status: false,
        startTime: '',
        endTime: '',
        message: maintenance.message
      });
      
      setMaintenance(prev => ({
        ...prev,
        status: false,
        startTime: '',
        endTime: ''
      }));
      
      setCountdown('Maintenance ended');
      toast.info('Maintenance period ended automatically');
    } catch (error) {
      console.error('Error auto-disabling maintenance:', error);
    }
  }, [maintenance.message]);

  // Load settings on mount
  useEffect(() => {
    fetchMaintenanceSettings();
  }, [fetchMaintenanceSettings]);

  // Set up countdown interval
  useEffect(() => {
    let interval;
    if (maintenance.status && maintenance.endTime) {
      // Initial check
      checkMaintenanceStatus();
      // Set up interval
      interval = setInterval(() => {
        checkMaintenanceStatus();
      }, 1000);
    } else {
      setCountdown('No active maintenance');
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [maintenance.status, maintenance.endTime, checkMaintenanceStatus]);

  const handleToggle = (e) => {
    const checked = e.target.checked;
    setMaintenance(prev => ({ ...prev, status: checked }));
    
    // Reset times when toggling on
    if (checked) {
      setMaintenance(prev => ({ ...prev, startTime: '', endTime: '' }));
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setMaintenance(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (maintenance.status) {
      if (!maintenance.startTime) {
        toast.error('Please select start time');
        return;
      }
      if (!maintenance.endTime) {
        toast.error('Please select end time');
        return;
      }
      if (maintenance.startTime >= maintenance.endTime) {
        toast.error('End time must be after start time');
        return;
      }
    }
    
    setLoading(true);
    
    try {
      const response = await api.put('/admin/settings/maintenance', {
        status: maintenance.status,
        startTime: maintenance.startTime,
        endTime: maintenance.endTime,
        message: maintenance.message
      });
      
      toast.success(response.data.message || 'Maintenance settings saved successfully!');
      
      if (maintenance.status && maintenance.endTime) {
        checkMaintenanceStatus();
      } else {
        setCountdown('No active maintenance');
      }
      
      // Refresh settings to get updated data
      await fetchMaintenanceSettings();
      
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to save maintenance settings');
    } finally {
      setLoading(false);
    }
  };

  const handleForceDisable = async () => {
    if (window.confirm('Are you sure you want to force disable maintenance mode?')) {
      setLoading(true);
      try {
        const response = await api.put('/admin/settings/maintenance', {
          status: false,
          startTime: '',
          endTime: '',
          message: maintenance.message
        });
        
        setMaintenance(prev => ({
          ...prev,
          status: false,
          startTime: '',
          endTime: ''
        }));
        
        setCountdown('No active maintenance');
        toast.success(response.data.message || 'Maintenance mode disabled successfully!');
        
        // Refresh settings
        await fetchMaintenanceSettings();
        
      } catch (error) {
        toast.error(error.response?.data?.error || 'Failed to disable maintenance');
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <div className="Maintenance-Mode-page">
      <div className="container-fluid">
        <div className="page-hedding-header">
          <h1 className="page-heading h3 text-uppercase fs-4">Maintenance Mode</h1>
        </div>

        <div className="admin-card-one">
          <div className="admin-card-header d-flex justify-content-between align-items-center">
            <h2 className="admin-card-heading">Maintenance Settings</h2>
            <span className={`badge ${maintenance.status ? 'bg-success' : 'bg-danger'}`}>
              {maintenance.status ? 'Active' : 'Inactive'}
            </span>
          </div>

          <div className="admin-card-body">
            <form onSubmit={handleSubmit}>
              <div className="round-card mb-4">
                <div className="enavle-row d-flex justify-content-between align-items-center">
                  <div>
                    <h6 className="mb-1">Enable Maintenance Mode</h6>
                    <small className="text-muted">Users will not be able to access the app</small>
                  </div>
                  <div className="form-check form-switch">
                    <input
                      className="form-check-input maintenance-switch"
                      type="checkbox"
                      id="toggleSwitch"
                      checked={maintenance.status}
                      onChange={handleToggle}
                      style={{ width: '40px', height: '20px', cursor: 'pointer' }}
                    />
                  </div>
                </div>
              </div>
              
              <div className="row g-3">
                <div className="col-md-6">
                  <label className="admin-label-one">Start Time</label>
                  <input
                    type="time"
                    name="startTime"
                    value={maintenance.startTime}
                    onChange={handleChange}
                    className="form-control admin-input-one"
                    disabled={!maintenance.status}
                    required={maintenance.status}
                  />
                </div>

                <div className="col-md-6">
                  <label className="admin-label-one">End Time</label>
                  <input
                    type="time"
                    name="endTime"
                    value={maintenance.endTime}
                    onChange={handleChange}
                    className="form-control admin-input-one"
                    disabled={!maintenance.status}
                    required={maintenance.status}
                  />
                </div>

                <div className="col-12">
                  <label className="admin-label-one">Maintenance Message</label>
                  <textarea
                    name="message"
                    value={maintenance.message}
                    onChange={handleChange}
                    className="form-control admin-input-one"
                    rows="3"
                    placeholder="App is under maintenance..."
                    disabled={!maintenance.status}
                  />
                </div>

                <div className="col-12">
                  <div className={`Maintenance-countdown ${maintenance.status ? 'active' : 'inactive'}`}>
                    {countdown}
                  </div>
                </div>

                <div className="col-12">
                  <button 
                    type="submit" 
                    className="admin-submit-one w-100" 
                    disabled={loading}
                  >
                    {loading ? 'Saving...' : 'Save Settings'}
                  </button>
                </div>
                
                <div className="col-12">
                  <button
                    type="button"
                    onClick={handleForceDisable}
                    className="btn btn-outline-danger w-100 mt-2"
                    disabled={!maintenance.status || loading}
                  >
                    ❌ Force Disable Maintenance
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MaintenanceMode;